import type { ReactNode } from 'react'
import type { IndexDef } from '@/api/types/readiness'
import { glossaryFor } from '@/lib/indexes/registry'
import { useInfoTooltip } from '@/components/readiness/useInfoTooltip'

// ── InfoTooltip primitive (Iteration 5 §17.1, Note 7) ───────────────────────
// Tooltips explain; they never contain an action. One primitive implements the
// rules: ~300ms open delay, `pointer-events: none`, placed above or below its
// trigger so it never covers it, opens on keyboard focus, closes on Escape,
// attached with `aria-describedby`. The jargon `Term` (I1 §4.3), the score and
// band tooltips, the card and ◆ tooltips, the header and flag tooltips are all
// compositions of it.

// Component form: a focusable trigger (button by default) wrapping the children.
// `plain` drops the dotted underline (icons, chips, whole values).
export function InfoTooltip({ content, children, className = '', width, plain, onOpen, testId, as = 'button', label }: { content: ReactNode; children: ReactNode; className?: string; width?: number; plain?: boolean; onOpen?: () => void; testId?: string; as?: 'button' | 'span'; label?: string }) {
  const { triggerProps, node } = useInfoTooltip<HTMLElement>({ content, width, onOpen, testId })
  const cls = `rd-term ${plain ? '!no-underline' : ''} ${className}`
  if (as === 'span') {
    return (
      <>
        <span {...(triggerProps as object)} tabIndex={0} className={cls} aria-label={label}>{children}</span>
        {node}
      </>
    )
  }
  return (
    <>
      <button type="button" {...(triggerProps as object)} className={cls} aria-label={label}>{children}</button>
      {node}
    </>
  )
}

// Plain-text tooltip (I1 §4.3): copy from a string, optional reference.
export function Tooltip({ text, reference, children, className = '', as }: { text: string; reference?: string | null; children: ReactNode; className?: string; as?: 'button' | 'span' }) {
  return (
    <InfoTooltip className={className} as={as} content={<>{text}{reference && <span style={{ color: 'var(--rd-text-muted)' }}> ({reference})</span>}</>}>
      {children}
    </InfoTooltip>
  )
}

export function Term({ index, term, children, className, as }: { index: IndexDef; term: string; children?: ReactNode; className?: string; as?: 'button' | 'span' }) {
  const entry = glossaryFor(index, term)
  if (!entry) return <span title={term}>{children ?? term}</span>
  return (
    <Tooltip text={entry.text} reference={entry.ref} className={className} as={as}>
      {children ?? term}
    </Tooltip>
  )
}

// Titled tooltip body used by the Iteration 5 compositions: bold title line,
// then one or more lines.
export function TipBody({ title, children }: { title?: ReactNode; children?: ReactNode }) {
  return (
    <div className="space-y-1 text-left text-[12px] leading-snug">
      {title && <p className="font-semibold" style={{ color: 'var(--rd-text-primary)' }}>{title}</p>}
      {children}
    </div>
  )
}
